import { defineStore } from 'pinia';
import { useOnceStore } from './index';
import { useCheckoutStore } from './checkout';
import { useAddOnStore } from './add-on';
import {
  CHANNEL,
  SUBSCRIPTION,
  addOnOptionalServices,
} from '@apps-once/definitions';
import {
  PAYMENT_FREQUENCY,
  SubscriptionPlan,
  addOns as billingAddOns,
  annualAddOns,
  AIO_HR_ADD_ON_IDS,
  AIO_PAYROLL_ADD_ON_IDS,
} from '@apps-billing-shared/definitions';
import { AddOnId } from '@apps-billing-shared/definitions';

// US payroll is a post-paid self-serve add-on (WO-3001) and must NOT
// appear in the initial-plan-purchase / trial-conversion checkout. The
// legacy `payroll-standard` upgrade path has no US billing identifier
// mapping (AddOns::ALL_ADD_ONS_MAPPING.payroll-standard.billingIdentifier
// has AU entries only), so leaving any of these variants in the cart
// would trip "No Zuora product identifier found" on commit.
const US_PAYROLL_ADD_ONS_HIDDEN_PRE_SUBSCRIBE: ReadonlyArray<string> = [
  billingAddOns.PAYROLL_ADD_ON_ID,
  annualAddOns.PAYROLL_ANNUAL_ADD_ON_ID,
  annualAddOns.PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID,
];
import { AddOn } from '@apps-once/types/checkout.d';
import { useQuoteStore } from './quote';
import { usePlansStore } from '@apps-billing-shared/store/plans';
import { isAddOnAvailableByPreSubscribeFeatureFlag } from '@/apps/once/src/util/pre-subscribed-validation';

type CartStoreState = {
  initialCartPlan: SubscriptionPlan; // this is the plan which is set when the cart is initialized
  cartPlan: SubscriptionPlan; // this is the plan which can be set manually in the cart
  addOns: Array<AddOnId>;
  // Map of parent add-on id -> array of optional-service keys in the cart for that add-on.
  // Optional services are always scoped to a specific add-on (see `addOnOptionalServices`
  // in `@apps-once/definitions`), so we preserve that relationship here.
  optionalServices: Partial<Record<AddOnId, string[]>>;
  annualUserCount: number;
  promoCode: string;
};

export const useCartStore = defineStore('once:cart', {
  state(): CartStoreState {
    return {
      initialCartPlan: SUBSCRIPTION.PREMIUM_YEARLY,
      cartPlan: SUBSCRIPTION.PREMIUM_YEARLY,
      addOns: [],
      optionalServices: {},
      annualUserCount: 1,
      promoCode: '',
    };
  },
  getters: {
    // This is the actual plan which dictates the context of the cart.
    // It is a computed - it will be based on the onceStore, or the manually set cartPlan
    // depending on the checkout step.
    plan(): SubscriptionPlan {
      if (useCheckoutStore().isAddOnPurchaseStep) {
        // Use the plan that the tenant is currently on
        return useOnceStore().plan;
      }
      // Otherwise, use the plan that is set in the cart
      return this.cartPlan;
    },
    hasBillingOptions(): boolean {
      return usePlansStore().hasBillingOptions(this.plan);
    },
    hasAddOns(): boolean {
      return this.addOns.length > 0;
    },
    isMonthly(): boolean {
      return usePlansStore().isMonthly(this.plan);
    },
    isAnnual(): boolean {
      return usePlansStore().isAnnual(this.plan);
    },
    isAnnualPaidMonthly(): boolean {
      return usePlansStore().isAnnualPaidMonthly(this.plan);
    },
    isAnnualUpfront(): boolean {
      return usePlansStore().isAnnualUpfront(this.plan);
    },
    canPurchaseUsers(): boolean {
      return usePlansStore().canPurchaseUsers(this.plan);
    },
    hasActiveUserBilling(): boolean {
      return usePlansStore().hasActiveUserBilling(this.plan);
    },
    // Monthly AddOns here relate to the Monthly billing cycle.
    // This includes AddOns which have weekly or monthly period.
    monthlyAddOns(): Array<AddOnId> {
      return this.addOns.filter(
        addOnId => !useAddOnStore().isAnnualAddOn(addOnId)
      );
    },
    annualAddOns(): Array<AddOnId> {
      return this.addOns.filter(addOnId =>
        useAddOnStore().isAnnualAddOn(addOnId)
      );
    },
    hasMonthlyBilledAddOns(): boolean {
      return this.monthlyAddOns.length > 0;
    },
    hasAnnuallyBilledAddOns(): boolean {
      return this.annualAddOns.length > 0;
    },
    planName(): string {
      const planStore = usePlansStore();
      return planStore.getBasePlanName(this.plan);
    },
    planPrice(): number {
      return Number(useOnceStore().getPlanPrice(this.plan));
    },
    isPremiumPlan(): boolean {
      return (
        this.plan === SUBSCRIPTION.PREMIUM ||
        this.plan === SUBSCRIPTION.PREMIUM_YEARLY
      );
    },
    isFlexiPlan(): boolean {
      return this.plan === SUBSCRIPTION.FLEXI;
    },
    weeklyCharge(): number {
      return useOnceStore().getWeeklyCharge(this.plan);
    },
    weeklyInactiveCharge(): number {
      return useOnceStore().getWeeklyInactiveCharge(this.plan);
    },
    addOnsAvailableForPlan(): Array<AddOn> {
      return useOnceStore().getPlanAvailableAddOns(this.plan);
    },
    // Availability is priced per plan (getAddOnPricing is keyed on planId), so
    // callers asking about a plan other than the current selection — e.g. a CTA
    // that switches the tenant onto a different plan — must pass that plan
    // rather than read the current-selection getter below.
    addOnsAvailableForTrialUserByPlan() {
      return (plan: SubscriptionPlan): Array<AddOn> => {
        const onceStore = useOnceStore();
        const isTrialPlan = onceStore.isFreePlan;
        const addOnsAvailableForPlan = onceStore.getPlanAvailableAddOns(plan);

        const withoutUsPayroll = (addOn: AddOn): boolean =>
          !onceStore.isCountryUS ||
          !US_PAYROLL_ADD_ONS_HIDDEN_PRE_SUBSCRIBE.includes(addOn.addOnId);

        if (!isTrialPlan) {
          return addOnsAvailableForPlan.filter(withoutUsPayroll);
        }

        // Otherwise, if it's a trial plan, filter the add-ons
        return addOnsAvailableForPlan
          .filter((addOn: AddOn) =>
            isAddOnAvailableByPreSubscribeFeatureFlag(addOn)
          )
          .filter(withoutUsPayroll);
      };
    },
    addOnsAvailableForTrialUser(): Array<AddOn> {
      return this.addOnsAvailableForTrialUserByPlan(this.plan);
    },
    /**
     * Returns an array of add-ons that are both available for the current plan and have been added to the cart.
     * This acts as a security measure to prevent users from adding ineligible add-ons via URL query parameters.
     *
     * @return {Array<AddOn>} An array of add-on objects that meet both criteria:
     * - Available for the current plan (from addOnsAvailableForPlan)
     * - Present in the cart (from addOns array)
     *
     * @example
     * // If addOnsAvailableForPlan contains [addOn1, addOn2, addOn3]
     * // and addOns contains ['addOn1', 'addOn3']
     * // then returns [addOn1, addOn3]
     *
     * @security
     * This function is for preventing users from adding add-ons that are not eligible
     * for their current plan, even if they attempt to do so through URL manipulation.
     */
    addOnsAvailableForCheckout(): Array<AddOn> {
      return this.addOnsAvailableForPlan.filter(
        (addOn: AddOn) =>
          this.addOns.includes(addOn.addOnId) ||
          this.addOns.includes(addOn.equivalentPaymentFrequencyPlans.annual) ||
          this.addOns.includes(
            addOn.equivalentPaymentFrequencyPlans.annualPaidMonthly
          )
      );
    },
    hasPlanAttachedTax(): boolean {
      return useOnceStore().hasPlanAttachedTax(this.plan);
    },
    /**
     * Detects if we're in an annual plan upgrade scenario (mid-contract upgrade)
     * This matches the logic from bl-subscription-summary-pricing.vue
     * @return {boolean} True if the current scenario is an annual plan upgrade, false otherwise
     */
    isAnnualUpfrontPlanUpgradeScenario(): boolean {
      const onceStore = useOnceStore();
      const checkoutStore = useCheckoutStore();
      const plansStore = usePlansStore();

      if (
        !checkoutStore.isChangePlanFlow ||
        onceStore.isAnnualSubscriptionEndingWithinNext31Days()
      ) {
        return false;
      }

      const currentPlan = onceStore.plan;
      const targetPlan = this.plan;

      const isCurrentPlanAnnualUpfront =
        plansStore.isAnnualUpfront(currentPlan);
      const isTargetPlanAnnualUpfront = plansStore.isAnnualUpfront(targetPlan);
      const isUpgradeToHigherTier = plansStore.isHigherTierPlan(
        currentPlan,
        targetPlan
      );

      return (
        isCurrentPlanAnnualUpfront &&
        isTargetPlanAnnualUpfront &&
        isUpgradeToHigherTier
      );
    },
    isAnnualPaidMonthlyPlanUpgradeScenario(): boolean {
      const onceStore = useOnceStore();
      const checkoutStore = useCheckoutStore();
      const plansStore = usePlansStore();

      if (
        !checkoutStore.isChangePlanFlow ||
        onceStore.isAnnualSubscriptionEndingWithinNext31Days()
      ) {
        return false;
      }

      const currentPlan = onceStore.plan;
      const targetPlan = this.plan;

      const isCurrentPlanAnnualPaidMonthly =
        plansStore.isAnnualPaidMonthly(currentPlan);
      const isTargetPlanAnnualPaidMonthly =
        plansStore.isAnnualPaidMonthly(targetPlan);
      const isUpgradeToHigherTier = plansStore.isHigherTierPlan(
        currentPlan,
        targetPlan
      );

      return (
        isCurrentPlanAnnualPaidMonthly &&
        isTargetPlanAnnualPaidMonthly &&
        isUpgradeToHigherTier
      );
    },
    isAnnualToAnnualChangePlanScenario(): boolean {
      const onceStore = useOnceStore();
      const checkoutStore = useCheckoutStore();
      const plansStore = usePlansStore();

      if (!checkoutStore.isChangePlanFlow) {
        return false;
      }

      const currentPlan = onceStore.plan;
      const targetPlan = this.plan;

      const isCurrentPlanAnnual = plansStore.isAnnual(currentPlan);
      const isTargetPlanAnnual = plansStore.isAnnual(targetPlan);

      return isCurrentPlanAnnual && isTargetPlanAnnual;
    },
  },
  actions: {
    setAnnualUserCount(count: number): void {
      const minimum = useOnceStore().getPlanMinimumUserCount(this.plan);
      this.annualUserCount = Math.max(count, minimum);
    },
    incrementAnnualUserCount(): void {
      const newCount = this.annualUserCount + 1;
      this.setAnnualUserCount(newCount);
    },
    isPlanSelected(plan: SubscriptionPlan): boolean {
      return Number(this.plan) === Number(plan);
    },
    decrementAnnualUserCount(): void {
      const newCount = this.annualUserCount - 1;
      this.setAnnualUserCount(newCount);
    },
    setInitialAnnualUserCount(): void {
      const onceStore = useOnceStore();

      if (
        onceStore.getFeatureFlag('wo-2826-apple-to-deputy-self-serve') &&
        onceStore.channel === CHANNEL.APPSTORE_APPLE
      ) {
        const initialCount =
          onceStore.subscription?.mobileMetadata?.maxUserAllowed ??
          onceStore.totalEmployeeCount ??
          0;

        this.setAnnualUserCount(initialCount);
        return;
      }

      // AIO apply-bundle: the tenant keeps their existing annual subscription
      // untouched, so the user count is locked to what they pre-paid. The order
      // summary must price that same number — apply-bundle charges the pre-paid
      // count, never headcount — so seed it directly rather than max()-ing in
      // headcount, which for an overage tenant would show a different figure
      // here than the locked pre-paid row and a total apply-bundle never bills.
      if (useCheckoutStore().isAioBundleApplyOnly) {
        this.setAnnualUserCount(onceStore.getPrePurchasedUserCountFromPlan());
        return;
      }

      const currentTotalEmployee = onceStore.totalEmployeeCount;
      const currentPrePaidUser = onceStore.getPrePurchasedUserCountFromPlan();
      const minimumPrePaidUserRequirement = onceStore.getPlanMinimumUserCount(
        this.plan
      );

      // Use the store's default value as fallback for any falsy value (undefined, null, 0, NaN)
      const currentAnnualUserCount = this.annualUserCount || 1;

      this.annualUserCount = Math.max(
        currentTotalEmployee,
        currentPrePaidUser,
        minimumPrePaidUserRequirement,
        currentAnnualUserCount
      );
    },
    /**
     * Adds current annual add-ons to the cart when subscription data is loaded
     * This is a private method called by setInitialState
     * @private
     */
    _addCurrentAnnualAddOnsToCart(): void {
      const addOns: string[] = useOnceStore().getCurrentActiveAddOns();

      if (!addOns || addOns.length === 0) {
        return;
      }

      const addOnStore = useAddOnStore();
      const targetFrequency: string = this._getTargetFrequency(this.plan);

      const annualAddOnsToAdd: string[] = addOns
        .filter((addOnId: string) => addOnStore.isAnnualAddOn(addOnId))
        .map((addOnId: string) => {
          const equivalentAddOnId: string =
            addOnStore.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
              addOnId,
              targetFrequency
            );
          return equivalentAddOnId || addOnId;
        });

      if (annualAddOnsToAdd.length > 0) {
        this.addOns.push(...annualAddOnsToAdd);
        this.addOns = addOnStore.sortAddOnsByOrdering(this.addOns);
      }
    },

    /**
     * Sets the initial state of the cart after subscription data is loaded
     * This includes setting the initial annual user count and adding current annual add-ons
     */
    setInitialState(): void {
      this.setInitialAnnualUserCount();
      // Special handling for addons for the change plan flow in the last 30 days or on the trial checkout
      if (
        useOnceStore().isAnnualSubscriptionEndingWithinNext31Days() &&
        useCheckoutStore().isChangePlanFlow
      ) {
        this.addOns = [];
        if (this.isAnnualToAnnualChangePlanScenario) {
          this._addCurrentAnnualAddOnsToCart();
        }
      } else if (!useCheckoutStore().isAddOnPurchaseStep) {
        this._convertAddOnsForPlan(this.plan);
      }
    },

    /**
     * Sets the plan in the cart and handles related updates
     * - Removes any existing discounts for the current plan
     * - Converts add-ons to match the new plan's billing frequency (if not in add-on checkout)
     * - Updates annual user count to minimum when switching to yearly plans
     *
     * @param {SubscriptionPlan} plan - The subscription plan to set
     */
    setPlan(plan: SubscriptionPlan): void {
      useQuoteStore().removeDiscount({ planId: this.plan });
      this.cartPlan = plan;
      this.setInitialState();
    },

    setInitialPlan(plan: SubscriptionPlan): void {
      this.initialCartPlan = plan;
      this.setPlan(plan);
    },

    /**
     * Seeds the bundle-locked HR and Payroll add-ons for the AIO flow and
     * aligns them to the current base plan's billing frequency. Idempotent:
     * a family already in the cart (in any frequency variant) is skipped, so
     * repeat activations don't stack duplicate variants. Once seeded, the
     * usual setPlan -> _convertAddOnsForPlan path keeps them in sync as the
     * base plan's schedule changes.
     */
    addAioBundleAddOns(): void {
      const addOnStore = useAddOnStore();

      const bundledFamilies = [
        { standardId: billingAddOns.HR_ADD_ON_ID, variants: AIO_HR_ADD_ON_IDS },
        {
          standardId: billingAddOns.PAYROLL_ADD_ON_ID,
          variants: AIO_PAYROLL_ADD_ON_IDS,
        },
      ];

      bundledFamilies.forEach(({ standardId, variants }) => {
        const alreadyInCart = this.addOns.some((id: AddOnId) =>
          variants.includes(id)
        );
        if (!alreadyInCart) {
          this.addOns.push(standardId);
        }
      });

      this.addOns = addOnStore.sortAddOnsByOrdering(this.addOns);

      // Match the base plan's billing schedule (monthly / annual variants).
      this._convertAddOnsForPlan(this.plan);

      // Bundled HR/Payroll are excluded from promo stacking (product decision).
      // Clear any stale auto-applied discount on them: _convertAddOnsForPlan
      // only strips it when the schedule changes, so a direct monthly
      // aio_bundle URL would otherwise deduct a promo on top of the 15% bundle
      // discount.
      const quoteStore = useQuoteStore();
      [...AIO_HR_ADD_ON_IDS, ...AIO_PAYROLL_ADD_ON_IDS].forEach(addOnId => {
        quoteStore.removeDiscount({ addOnId: addOnId as AddOnId });
      });
    },

    /**
     * Adds carried-over add-ons (Analytics+/Messaging+) into the bundle cart,
     * preserving each one's own billing schedule from standard checkout. Unlike
     * the bundled HR/Payroll (seeded and coerced to the base plan by
     * addAioBundleAddOns), these non-bundle add-ons keep the schedule the user
     * selected — a monthly Analytics+ stays monthly under an annual base.
     * The one exception: annual add-ons can't sit under a monthly base, so when
     * the base plan is monthly we coerce them down to monthly.
     * Used when redirecting standard checkout into the aio_bundle flow.
     */
    addBundleCarriedAddOns(addOnIds: AddOnId[]): void {
      const addOnStore = useAddOnStore();
      addOnIds.forEach((id: AddOnId) => {
        if (!this.addOns.includes(id)) {
          this.addOns.push(id);
        }
      });
      this.addOns = addOnStore.sortAddOnsByOrdering(this.addOns);

      if (usePlansStore().isMonthly(this.plan)) {
        this._convertAddOnsForPlan(this.plan);
      }
    },

    /**
     * Converts add-ons in the cart to match the billing frequency of the new plan
     * @private
     * @param {SubscriptionPlan} plan - The new plan to convert add-ons for
     */
    _convertAddOnsForPlan(plan: SubscriptionPlan): void {
      const addOnStore = useAddOnStore();
      const quoteStore = useQuoteStore();

      // Determine target frequency based on new plan
      const targetFrequency: string = this._getTargetFrequency(plan);
      if (!targetFrequency) {
        return;
      }

      // Convert add-ons to their equivalent payment frequency and remove discounts for converted ones
      this.addOns = this.addOns.map((addOn: AddOnId) => {
        const equivalent: string =
          addOnStore.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
            addOn,
            targetFrequency
          );

        if (equivalent && equivalent !== addOn) {
          // Remove discount before and after conversion to ensure no duplicate discounts are applied
          quoteStore.removeDiscount({ addOnId: addOn });
          quoteStore.removeDiscount({ addOnId: equivalent as AddOnId });
          this.clearOptionalServicesForAddOn(addOn, equivalent as AddOnId);
          return equivalent as AddOnId;
        }

        return addOn;
      });

      // Sort after conversion to ensure order is maintained
      this.addOns = addOnStore.sortAddOnsByOrdering(this.addOns);
    },
    /**
     * Helper method to determine the target payment frequency based on the new plan
     *
     * @param {SubscriptionPlan} plan - The plan to determine frequency for
     * @return {string} The target payment frequency ('monthly', 'annualPaidMonthly', 'annual', or empty string)
     */
    _getTargetFrequency(plan: SubscriptionPlan): string {
      const plansStore = usePlansStore();
      if (plansStore.isMonthly(plan)) {
        return PAYMENT_FREQUENCY.MONTHLY;
      }
      if (plansStore.isAnnualPaidMonthly(plan)) {
        return PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY;
      }
      if (plansStore.isAnnualUpfront(plan)) {
        return PAYMENT_FREQUENCY.ANNUAL_UPFRONT;
      }
      return '';
    },
    isAddOnInCart(addOnId: AddOnId): boolean {
      return this.addOns.includes(addOnId);
    },
    isOptionalServiceInCart(serviceKey: string): boolean {
      return Object.values(this.optionalServices).some(serviceKeys =>
        (serviceKeys ?? []).includes(serviceKey)
      );
    },
    /**
     * Adds an optional service to the cart, scoped under its parent add-on.
     * The parent add-on is looked up from `addOnOptionalServices`
     * (in `@apps-once/definitions`). No-op if the service key is unknown,
     * its parent add-on is not in the cart, or it is already in the cart.
     */
    addOptionalService(serviceKey: string): void {
      type OptionalServiceAddOnKey = keyof typeof addOnOptionalServices;
      const parentAddOnId = (
        Object.keys(addOnOptionalServices) as OptionalServiceAddOnKey[]
      ).find(addOnId =>
        (addOnOptionalServices[addOnId] ?? []).some(
          service => service.key === serviceKey
        )
      );
      if (!parentAddOnId) {
        return;
      }

      // Optional services can't exist in the cart without their parent add-on.
      if (!this.addOns.includes(parentAddOnId)) {
        return;
      }

      const services = this.optionalServices[parentAddOnId] ?? [];
      if (services.includes(serviceKey)) {
        return;
      }
      this.optionalServices[parentAddOnId] = [...services, serviceKey];
    },
    /**
     * Removes an optional service from the cart, regardless of which parent
     * add-on it was registered under. Cleans up the parent entry when its
     * last service is removed.
     */
    removeOptionalService(serviceKey: string): void {
      for (const addOnId of Object.keys(this.optionalServices) as AddOnId[]) {
        const services = this.optionalServices[addOnId];
        if (!services) {
          continue;
        }
        const index = services.indexOf(serviceKey);
        if (index === -1) {
          continue;
        }
        const next = services.filter((_, i) => i !== index);
        if (next.length === 0) {
          delete this.optionalServices[addOnId];
        } else {
          this.optionalServices[addOnId] = next;
        }
        return;
      }
    },
    clearOptionalServicesForAddOn(
      addOnId: AddOnId,
      equivalentAddOnId?: AddOnId
    ): void {
      delete this.optionalServices[addOnId];

      const monthlyEquivalent =
        equivalentAddOnId ??
        (useAddOnStore().getAddOnEquivalentPaymentFrequencyPlanIdentifier(
          addOnId,
          PAYMENT_FREQUENCY.MONTHLY
        ) as AddOnId | undefined);

      if (monthlyEquivalent) {
        delete this.optionalServices[monthlyEquivalent];
      }
    },
    isFreeWithPlan(addOnId: AddOnId): boolean {
      const addOnStore = useAddOnStore();

      return addOnStore.isFreeWithPlan(addOnId, this.plan);
    },
    isAnnualAddOn(addOnId: AddOnId): boolean {
      return this.annualAddOns.includes(addOnId);
    },
    isMonthlyAddOn(addOnId: AddOnId): boolean {
      return this.monthlyAddOns.includes(addOnId);
    },
    addToCart(addOnId: AddOnId): void {
      if (!this.addOns.includes(addOnId)) {
        this.addOns.push(addOnId);
        this.addOns = useAddOnStore().sortAddOnsByOrdering(this.addOns);
      }
    },
    removeFromCart(addOnId: AddOnId): void {
      const index = this.addOns.indexOf(addOnId);
      if (index > -1) {
        this.addOns.splice(index, 1);
        useQuoteStore().removeDiscount({ addOnId });
        this.clearOptionalServicesForAddOn(addOnId);
        this.addOns = useAddOnStore().sortAddOnsByOrdering(this.addOns);
        // Optional services are scoped to a parent add-on, so removing the
        // parent must also drop any optional services attached to it.
        delete this.optionalServices[addOnId];
      }
    },
    removeAllAddOnsFromCart(): void {
      const quoteStore = useQuoteStore();
      // Remove promo discounts for all add-ons before clearing the cart
      this.addOns.forEach((addOnId: AddOnId) => {
        quoteStore.removeDiscount({ addOnId });
      });
      this.addOns = [];
      this.optionalServices = {};
    },
    emptyPromoCode(): void {
      this.promoCode = '';
    },
    reset(): void {
      this.cartPlan = SUBSCRIPTION.PREMIUM_YEARLY;
      useAddOnStore().clearAddOn();
      this.addOns = [];
      this.optionalServices = {};
      this.annualUserCount = 1;
      this.promoCode = '';
      // A fresh checkout starts clean: drop any promo discounts (including
      // auto-applied ones) carried over from a previous checkout.
      useQuoteStore().clearAllPromoDiscounts();
    },
    /**
     * Checks if this is a mid-contract upgrade scenario
     * - User is upgrading from one annual plan to another annual plan
     * - Not in the last 31 days of their current plan
     *
     * @param {number} newPlanId - The new plan ID to check
     * @return {boolean} Whether this is a mid-contract upgrade
     */
    isMidContractUpgrade(newPlanId: number): boolean {
      const onceStore = useOnceStore();
      const checkoutStore = useCheckoutStore();
      const plansStore = usePlansStore();

      if (!checkoutStore.isChangePlanFlow) {
        return false;
      }

      const currentPlan = onceStore.plan;
      const isCurrentPlanAnnual = plansStore.isAnnual(currentPlan);

      const isNewPlanAnnual = plansStore.isAnnual(newPlanId);

      const isCurrentPlanNotInLast31Days =
        !onceStore.isAnnualSubscriptionEndingWithinNext31Days();

      return (
        isCurrentPlanAnnual && isNewPlanAnnual && isCurrentPlanNotInLast31Days
      );
    },
  },
});
